import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { HomeComponent } from './home/home.component';
import { OnSaleComponent } from './on-sale/on-sale.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { WishlistComponent } from './wishlist/wishlist.component';
import { BlogComponent } from './blog/blog.component';
import { SearchComponent } from './search/search.component';
import { ImprintComponent } from './imprint/imprint.component';
import { ContactComponent } from './contact/contact.component';
import { DetailViewComponent } from './detail-view/detail-view.component';
import { DisclaimerComponent } from './disclaimer/disclaimer.component';
import { ErrorComponent } from './error/error.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { ServerDownComponent } from './server-down/server-down.component';
import { MyPurchasesComponent } from './my-purchases/my-purchases.component';
import { GiftCardsComponent } from './gift-cards/gift-cards.component';
import { CardDealDetailComponent } from './card-deal-detail/card-deal-detail.component';
import { ItunesMovieDealsComponent } from './itunes-movie-deals/itunes-movie-deals.component';
import { PrimeVideoDealsComponent } from './prime-video-deals/prime-video-deals.component';
import { VuduMovieDealsComponent } from './vudu-movie-deals/vudu-movie-deals.component';
import { ItunesMovies4KComponent } from './itunes-movies-4k/itunes-movies-4k.component';
import { RedirectGuard } from './RedirectGuard';
import { AppleRedirectComponent } from './apple-redirect/apple-redirect.component';
import { ReferralsComponent } from './referrals/referrals.component';
import { RedeemComponent } from './redeem/redeem.component';
import { NotificationSettingsComponent } from './notification-settings/notification-settings.component';
import { PreferencesComponent } from './preferences/preferences.component';
import { ActorsComponent } from './actors/actors.component';
import { StudiosComponent } from './studios/studios.component';
import { BlocklistComponent } from './blocklist/blocklist.component';
import { MyListsComponent } from './my-lists/my-lists.component';
import { CustomListDetailComponent } from './custom-list-detail/custom-list-detail.component';
import { CountryPollComponent } from './country-poll/country-poll.component';
import { AiLandingComponent } from './ai-landing/ai-landing.component';
import { ViewportScroller } from '@angular/common';
import { filter } from 'rxjs/operators';

export function publicListDetailMatcher(segments: UrlSegment[]): UrlMatchResult {
  const routeInfo = getPublicListRouteInfo(segments);
  if (!routeInfo || (segments.length !== routeInfo.itemIndex + 1 && segments.length !== routeInfo.itemIndex + 2)) {
    return null;
  }

  const listId = segments[routeInfo.itemIndex].path;
  if (!/^\d+$/.test(listId)) {
    return null;
  }

  if (segments.length === routeInfo.itemIndex + 2 && isKnownPublicCategoryRoute(segments)) {
    return null;
  }

  const posParams: any = {
    country: segments[0],
    store: segments[1],
    mediaType: segments[2],
    listId: segments[routeInfo.itemIndex]
  };

  if (segments.length === routeInfo.itemIndex + 2) {
    posParams.listSlug = segments[routeInfo.itemIndex + 1];
  }

  return {
    consumed: segments,
    posParams: posParams
  };
}

export function publicListCategoryMatcher(segments: UrlSegment[]): UrlMatchResult {
  const routeInfo = getPublicListRouteInfo(segments);
  if (!routeInfo || segments.length !== routeInfo.itemIndex + 2) {
    return null;
  }

  const categoryId = segments[routeInfo.itemIndex].path;
  if (!/^-?\d+$/.test(categoryId)) {
    return null;
  }

  const isPublicCategoryId = categoryId.charAt(0) === "-" || isKnownPublicCategoryRoute(segments);
  if (!isPublicCategoryId) {
    return null;
  }

  return {
    consumed: segments,
    posParams: {
      country: segments[0],
      store: segments[1],
      mediaType: segments[2],
      publicCategoryId: segments[routeInfo.itemIndex],
      publicCategorySlug: segments[routeInfo.itemIndex + 1],
      publicListRouteSection: segments[routeInfo.sectionIndex]
    }
  };
}

export function discoverListSectionMatcher(segments: UrlSegment[]): UrlMatchResult {
  if (!segments || segments.length !== 5 || !isDiscoverRouteSegment(segments[3].path) || !isDiscoverListSectionSegment(segments[4].path)) {
    return null;
  }

  return {
    consumed: segments,
    posParams: {
      country: segments[0],
      store: segments[1],
      mediaType: segments[2],
      discover: segments[3],
      publicListRouteSection: segments[4]
    }
  };
}

export function discoverListsBaseMatcher(segments: UrlSegment[]): UrlMatchResult {
  if (!segments || segments.length !== 4 || !isDiscoverRouteSegment(segments[3].path)) {
    return null;
  }

  return {
    consumed: segments,
    posParams: {
      country: segments[0],
      store: segments[1],
      mediaType: segments[2],
      discover: segments[3]
    }
  };
}

function getPublicListRouteInfo(segments: UrlSegment[]): any {
  if (!segments || segments.length < 5) {
    return null;
  }

  const listsSegment = segments[3].path;
  if (isLegacyPublicListSegment(listsSegment)) {
    return {
      sectionIndex: 3,
      itemIndex: 4
    };
  }

  if (segments.length >= 6 && isDiscoverRouteSegment(listsSegment) && isDiscoverListSectionSegment(segments[4].path)) {
    return {
      sectionIndex: 4,
      itemIndex: 5
    };
  }

  return null;
}

function isLegacyPublicListSegment(segment: string): boolean {
  return segment === "lists" ||
    segment === "listen" ||
    segment === "public-lists" ||
    segment === "community-lists";
}

function isDiscoverRouteSegment(segment: string): boolean {
  return segment === "discover" ||
    segment === "entdecken" ||
    segment === "decouvrir" ||
    segment === "descubrir";
}

function isDiscoverListSectionSegment(segment: string): boolean {
  return segment === "editorial-lists" ||
    segment === "redaktionelle-listen" ||
    segment === "listes-editoriales" ||
    segment === "listas-editoriales" ||
    segment === "community-lists";
}

function isKnownPublicCategoryRoute(segments: UrlSegment[]): boolean {
  const routeInfo = getPublicListRouteInfo(segments);
  if (typeof sessionStorage === "undefined" || !routeInfo || segments.length !== routeInfo.itemIndex + 2) {
    return false;
  }

  try {
    const value = sessionStorage.getItem(getPublicCategoryRouteStorageKey(segments));
    if (!value) {
      return false;
    }

    const saved = JSON.parse(value);
    const timestamp = Number(saved.timestamp);
    return !isNaN(timestamp) &&
      Date.now() - timestamp <= 30 * 60 * 1000 &&
      saved.path === getPublicCategoryRoutePath(segments);
  }
  catch (e) {
    return false;
  }
}

function getPublicCategoryRouteStorageKey(segments: UrlSegment[]): string {
  const routeInfo = getPublicListRouteInfo(segments);
  if (!routeInfo) {
    return "";
  }

  return "customListsCategoryRoute:v1:" +
    segments[0].path + ":" +
    segments[1].path + ":" +
    segments[2].path + ":" +
    getPublicCategoryRouteSectionKey(segments[routeInfo.sectionIndex].path) + ":" +
    segments[routeInfo.itemIndex].path + ":" +
    segments[routeInfo.itemIndex + 1].path;
}

function getPublicCategoryRouteSectionKey(routeSegment: string): string {
  return routeSegment === "community-lists" ? "community" : "public";
}

function getPublicCategoryRoutePath(segments: UrlSegment[]): string {
  const parts = [];
  for (let i = 0; i < segments.length; i++) {
    parts.push(segments[i].path);
  }
  return "/" + parts.join("/");
}

const appRoutes: Routes = [
  { path: '', redirectTo: '/us', pathMatch: 'full', data: { title: 'homeTitle', description: 'homeDesc', }},
  { path: '404', component: PageNotFoundComponent, data: { noindex: true } },
  { path: 'error', component: ErrorComponent },

  { path: 'apple_redirect', component: AppleRedirectComponent, data: { noindex: true } },
  { path: 'ai', redirectTo: '/us/ai', pathMatch: 'full' },
  { path: 'gpt', redirectTo: '/us/gpt', pathMatch: 'full' },

  { path: ':country/ai', component: AiLandingComponent, data: { title: 'CheapCharts for AI Agents', description: 'CheapCharts API guidance for AI agents and LLM tools. Use the GPT API for deals, prices, recommendations, charts, and topsellers instead of scraping.' } },
  { path: ':country/gpt', component: AiLandingComponent, data: { title: 'CheapCharts for AI Agents', description: 'CheapCharts API guidance for AI agents and LLM tools. Use the GPT API for deals, prices, recommendations, charts, and topsellers instead of scraping.' } },

  { path: ':country', component: HomeComponent, data: { title: 'homeTitle', description: 'homeDesc' }},

  { path: ':country/referrals', component: ReferralsComponent, data: { title: 'referralsTitle', description: 'referralsDesc' }},
  { path: ':country/einladungen', component: ReferralsComponent, data: { title: 'referralsTitle', description: 'referralsDesc' }},
  { path: ':country/invitations', component: ReferralsComponent, data: { title: 'referralsTitle', description: 'referralsDesc' }},

  { path: ':country/redeem', component: RedeemComponent, data: { title: 'redeemTitle', description: 'redeemDesc' }},
  { path: ':country/reclamer', component: RedeemComponent, data: { title: 'redeemTitle', description: 'redeemDesc' }},
  { path: ':country/einloesen', component: RedeemComponent, data: { title: 'redeemTitle', description: 'redeemDesc' }},

  { path: ':country/notifications', component: NotificationSettingsComponent, data: { noindex: true } },
  { path: ':country/benachrichtigungen', component: NotificationSettingsComponent, data: { noindex: true } },
  { path: ':country/notificaciones', component: NotificationSettingsComponent, data: { noindex: true } },

  { path: ':country/preferences', component: PreferencesComponent, data: { noindex: true } },
  { path: ':country/praeferenzen', component: PreferencesComponent, data: { noindex: true } },
  { path: ':country/preferencias', component: PreferencesComponent, data: { noindex: true } },

  { path: ':country/country-poll', component: CountryPollComponent, data: { title: 'countryPollTitle', description: 'countryPollDesc' }},

  { path: ':country/vudu-movie-deals', component: VuduMovieDealsComponent, data: { title: 'vuduMovieDealsTitle', description: 'vuduMovieDealsDesc' } },

  { path: ':country/on-sale', component: OnSaleComponent, canActivate: [RedirectGuard], data: { title: 'onSaleTitle', description: 'onSaleDesc' }},
  { path: ':country/im-angebot', component: OnSaleComponent, canActivate: [RedirectGuard], data: { title: 'onSaleTitle', description: 'onSaleDesc' }},
  { path: ':country/en-solde', component: OnSaleComponent, canActivate: [RedirectGuard], data: { title: 'onSaleTitle', description: 'onSaleDesc' }},
  { path: ':country/en-oferta', component: OnSaleComponent, canActivate: [RedirectGuard], data: { title: 'onSaleTitle', description: 'onSaleDesc' } },

  { path: ':country/:store/card-deal/:id', component: CardDealDetailComponent },
  { path: ':country/:store/card-deal/:id/:slug', component: CardDealDetailComponent },

  { path: ':country/:store/:mediaType/actors/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/actors/:ids/:slug', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/acteurs/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/acteurs/:ids/:slug', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/schauspieler/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/schauspieler/:ids/:slug', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/actores/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/actores/:ids/:slug', component: ActorsComponent, },

  { path: ':country/:store/:mediaType/screenwriters/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/screenwriters/:ids/:slug', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/scenaristes/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/scenaristes/:ids/:slug', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/drehbuchautoren/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/drehbuchautoren/:ids/:slug', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/guionistas/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/guionistas/:ids/:slug', component: ActorsComponent, },

  { path: ':country/:store/:mediaType/directors/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/directors/:ids/:slug', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/directeurs/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/directeurs/:ids/:slug', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/regisseure/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/regisseure/:ids/:slug', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/directores/:ids', component: ActorsComponent, },
  { path: ':country/:store/:mediaType/directores/:ids/:slug', component: ActorsComponent, },

  { path: ':country/:store/:mediaType/studios/:ids', component: StudiosComponent, },

  { path: ':country/:store/:mediaType/black-friday-deals', component: OnSaleComponent, data: { ignore: true } },
  { path: ':country/:store/:mediaType/soldes-black-friday', component: OnSaleComponent, data: { ignore: true } },
  { path: ':country/:store/:mediaType/ofertas-black-friday', component: OnSaleComponent, data: { ignore: true } },

  { path: ':country/:store/:mediaType/on-sale', component: OnSaleComponent, data: { title: 'onSaleTitle', description: 'onSaleDesc' }},
  { path: ':country/:store/:mediaType/im-angebot', component: OnSaleComponent, data: { title: 'onSaleTitle', description: 'onSaleDesc' }},
  { path: ':country/:store/:mediaType/en-solde', component: OnSaleComponent, data: { title: 'onSaleTitle', description: 'onSaleDesc' }},
  { path: ':country/:store/:mediaType/en-oferta', component: OnSaleComponent, data: { title: 'onSaleTitle', description: 'onSaleDesc' } },

  { path: ':country/:store/:mediaType/charts', component: OnSaleComponent, data: { title: 'chartsTitle', description: 'chartsDesc' }},
  // { path: ':country/:store/:mediaType/top-200', component: OnSaleComponent, data: { title: 'top200Title', description: 'top200Desc' } }, // Temporarily disabled
  { path: ':country/:store/:mediaType/coming-soon', component: OnSaleComponent, data: { title: 'comingSoonTitle', description: 'comingSoonDesc' } },
  { path: ':country/:store/:mediaType/new-releases', component: OnSaleComponent, data: { title: 'newReleasesTitle', description: 'newReleasesDesc' } },
  { path: ':country/:store/:mediaType/new-movies', component: OnSaleComponent, data: { title: 'newMoviesTitle', description: 'newMoviesDesc' } },
  { path: ':country/:store/:mediaType/new-seasons', component: OnSaleComponent, data: { title: 'newSeasonsTitle', description: 'newSeasonsDesc' } },
  { path: ':country/:store/:mediaType/4k', component: OnSaleComponent, data: { title: 'movies4KTitle', description: 'movies4KDesc' } },
  { path: ':country/:store/:mediaType/4k-seasons', component: OnSaleComponent, data: { title: 'seasons4KTitle', description: 'seasons4KDesc' } },
  { path: ':country/:store/:mediaType/new-to-4k', component: OnSaleComponent, data: { title: 'newTo4kTitle', description: 'newTo4kDesc' } },
  { path: ':country/:store/:mediaType/classic-seasons', component: OnSaleComponent, data: { title: 'classicSeasonsTitle', description: 'classicSeasonsDesc' } },

  { path: ':country/:store/:mediaType/all-movies', component: OnSaleComponent, data: { title: 'allMoviesTitle', description: 'allMoviesDesc' }},
  { path: ':country/:store/:mediaType/alle-filme', component: OnSaleComponent, data: { title: 'allMoviesTitle', description: 'allMoviesDesc' }},
  { path: ':country/:store/:mediaType/tous-les-films', component: OnSaleComponent, data: { title: 'allMoviesTitle', description: 'allMoviesDesc' }},
  { path: ':country/:store/:mediaType/todas-las-peliculas', component: OnSaleComponent, data: { title: 'allMoviesTitle', description: 'allMoviesDesc' } },
  {
    path: ':country/:store/:mediaType/new-on-prime-video',
    component: OnSaleComponent,
    data: { title: 'newOnPrimeVideoTitle', description: 'newOnPrimeVideoDesc' }
  },

  { path: ':country/:store/:mediaType/all-seasons', component: OnSaleComponent, data: { title: 'allSeasonsTitle', description: 'allSeasonsDesc'}},
  { path: ':country/:store/:mediaType/alle-serien', component: OnSaleComponent, data: { title: 'allSeasonsTitle', description: 'allSeasonsDesc'}},
  { path: ':country/:store/:mediaType/toutes-les-series', component: OnSaleComponent, data: { title: 'allSeasonsTitle', description: 'allSeasonsDesc'}},
  { path: ':country/:store/:mediaType/todas-las-temporadas', component: OnSaleComponent, data: { title: 'allSeasonsTitle', description: 'allSeasonsDesc' } },

  { path: ':country/:store/:mediaType/all-apps', component: OnSaleComponent, data: { title: 'allAppsTitle', description: 'allAppsDesc'}},
  { path: ':country/:store/:mediaType/alle-apps', component: OnSaleComponent, data: { title: 'allAppsTitle', description: 'allAppsDesc'}},
  { path: ':country/:store/:mediaType/toutes-les-apps', component: OnSaleComponent, data: { title: 'allAppsTitle', description: 'allAppsDesc'}},
  { path: ':country/:store/:mediaType/todas-las-apps', component: OnSaleComponent, data: { title: 'allAppsTitle', description: 'allAppsDesc' } },

  { path: ':country/:store/:mediaType/all-macapps', component: OnSaleComponent, data: { title: 'allMacAppsTitle', description: 'allMacAppsDesc' }},
  { path: ':country/:store/:mediaType/alle-macapps', component: OnSaleComponent, data: { title: 'allMacAppsTitle', description: 'allMacAppsDesc' }},
  { path: ':country/:store/:mediaType/toutes-les-applis-mac', component: OnSaleComponent, data: { title: 'allMacAppsTitle', description: 'allMacAppsDesc' }},
  { path: ':country/:store/:mediaType/todas-las-apps-mac', component: OnSaleComponent, data: { title: 'allMacAppsTitle', description: 'allMacAppsDesc' } },


  { path: ':country/:store/:mediaType/all-music', component: OnSaleComponent, data: { title: 'allMusicTitle', description: 'allMusicDesc'} },
  { path: ':country/:store/:mediaType/musik-komplett', component: OnSaleComponent, data: { title: 'allMusicTitle', description: 'allMusicDesc'} },
  { path: ':country/:store/:mediaType/toute-la-musique', component: OnSaleComponent, data: { title: 'allMusicTitle', description: 'allMusicDesc'} },
  { path: ':country/:store/:mediaType/toda-la-musica', component: OnSaleComponent, data: { title: 'allMusicTitle', description: 'allMusicDesc' } },

  { path: ':country/:store/:mediaType/all-books', component: OnSaleComponent, data: { title: 'allBooksTitle', description: 'allBooksDesc'} },
  { path: ':country/:store/:mediaType/alle-buecher', component: OnSaleComponent, data: { title: 'allBooksTitle', description: 'allBooksDesc'} },
  { path: ':country/:store/:mediaType/tous-les-livres', component: OnSaleComponent, data: { title: 'allBooksTitle', description: 'allBooksDesc'} },
  { path: ':country/:store/:mediaType/todos-los-libros', component: OnSaleComponent, data: { title: 'allBooksTitle', description: 'allBooksDesc' } },

  { path: ':country/:store/:mediaType/all-audiobooks', component: OnSaleComponent, data: { title: 'allAudiobooksTitle', description: 'allAudiobooksDesc' } },
  { path: ':country/:store/:mediaType/alle-hoerbuecher', component: OnSaleComponent, data: { title: 'allAudiobooksTitle', description: 'allAudiobooksDesc' } },
  { path: ':country/:store/:mediaType/tous-les-livres-audio', component: OnSaleComponent, data: { title: 'allAudiobooksTitle', description: 'allAudiobooksDesc' } },
  { path: ':country/:store/:mediaType/todos-los-audiolibros', component: OnSaleComponent, data: { title: 'allAudiobooksTitle', description: 'allAudiobooksDesc' } },

  { path: ':country/:store/:mediaType/movies-in-4k', component: OnSaleComponent, data: { title: 'moviesIn4kTitle', description: 'moviesIn4kDesc' }},
  { path: ':country/:store/:mediaType/filme-in-4k', component: OnSaleComponent, data: { title: 'moviesIn4kTitle', description: 'moviesIn4kDesc' }},
  { path: ':country/:store/:mediaType/films-en-4k', component: OnSaleComponent, data: { title: 'moviesIn4kTitle', description: 'moviesIn4kDesc' }},
  { path: ':country/:store/:mediaType/peliculas-en-4k', component: OnSaleComponent, data: { title: 'moviesIn4kTitle', description: 'moviesIn4kDesc' } },

  { path: ':country/:store/:mediaType/upgraded-to-4k', component: OnSaleComponent },
  { path: ':country/:store/:mediaType/auf-4k-aktualisiert', component: OnSaleComponent },
  { path: ':country/:store/:mediaType/maintenant-en-4k', component: OnSaleComponent },
  { path: ':country/:store/:mediaType/actualizado-a-4k', component: OnSaleComponent },

  { path: ':country/:store/:mediaType/movies-in-3d', component: OnSaleComponent, data: { title: 'moviesIn3dTitle', description: 'moviesIn3dDesc' } },
  { path: ':country/:store/:mediaType/filme-in-3d', component: OnSaleComponent, data: { title: 'moviesIn3dTitle', description: 'moviesIn3dDesc' } },
  { path: ':country/:store/:mediaType/films-en-3d', component: OnSaleComponent, data: { title: 'moviesIn3dTitle', description: 'moviesIn3dDesc' } },
  { path: ':country/:store/:mediaType/peliculas-en-3d', component: OnSaleComponent, data: { title: 'moviesIn3dTitle', description: 'moviesIn3dDesc' } },

  { path: ':country/:store/:mediaType/movie-bundles', component: OnSaleComponent, data: { title: 'movieBundlesTitle', description: 'movieBundlesDesc' }},
  { path: ':country/:store/:mediaType/film-bundles', component: OnSaleComponent, data: { title: 'movieBundlesTitle', description: 'movieBundlesDesc' }},
  { path: ':country/:store/:mediaType/paquets-films', component: OnSaleComponent, data: { title: 'movieBundlesTitle', description: 'movieBundlesDesc' }},
  { path: ':country/:store/:mediaType/paquetes-de-peliculas', component: OnSaleComponent, data: { title: 'movieBundlesTitle', description: 'movieBundlesDesc' } },

  { path: ':country/:store/:mediaType/season-bundles', component: OnSaleComponent },
  { path: ':country/:store/:mediaType/serien-bundles', component: OnSaleComponent },
  { path: ':country/:store/:mediaType/paquets-series', component: OnSaleComponent },
  { path: ':country/:store/:mediaType/paquetes-de-temporadas', component: OnSaleComponent },

  { path: ':country/:store/:mediaType/imdb-top-250', component: OnSaleComponent, data: { title: 'imdbTop250Title', description: 'imdbTop250Desc' }},

  { path: ':country/:store/:mediaType/just-released', component: OnSaleComponent, data: { title: 'justReleasedTitle', description: 'justReleasedDesc' }},
  { path: ':country/:store/:mediaType/jetzt-erhaeltlich', component: OnSaleComponent, data: { title: 'justReleasedTitle', description: 'justReleasedDesc' }},
  { path: ':country/:store/:mediaType/nouvellement-disponible', component: OnSaleComponent, data: { title: 'justReleasedTitle', description: 'justReleasedDesc' }},
  { path: ':country/:store/:mediaType/recien-lanzadas', component: OnSaleComponent, data: { title: 'justReleasedTitle', description: 'justReleasedDesc' } },

  { path: ':country/:store/:mediaType/preorder', component: OnSaleComponent, data: { title: 'preorderTitle', description: 'preorderDesc'}},
  { path: ':country/:store/:mediaType/vorbestellen', component: OnSaleComponent, data: { title: 'preorderTitle', description: 'preorderDesc'}},
  { path: ':country/:store/:mediaType/precommande', component: OnSaleComponent, data: { title: 'preorderTitle', description: 'preorderDesc'}},
  { path: ':country/:store/:mediaType/pre-pedido', component: OnSaleComponent, data: { title: 'preorderTitle', description: 'preorderDesc' } },

  { path: ':country/:store/:mediaType/greatest-hits', component: OnSaleComponent, data: { title: 'greatestHitsTitle', description: 'greatestHitsDesc' }},

  { path: ':country/:store/:mediaType/new-on-itunes', component: OnSaleComponent, data: { title: 'newOniTunesTitle', description: 'newOniTunesDesc' }},
  { path: ':country/:store/:mediaType/neu-bei-itunes', component: OnSaleComponent, data: { title: 'newOniTunesTitle', description: 'newOniTunesDesc' }},
  { path: ':country/:store/:mediaType/nouveau-sur-itunes', component: OnSaleComponent, data: { title: 'newOniTunesTitle', description: 'newOniTunesDesc' }},
  { path: ':country/:store/:mediaType/nuevo-en-itunes', component: OnSaleComponent, data: { title: 'newOniTunesTitle', description: 'newOniTunesDesc' } },

  { path: ':country/:store/:mediaType/vision-pro-apps', component: OnSaleComponent, data: { title: 'visionProAppsTitle', description: 'visionProAppsDesc' } },

  { path: ':country/:store/:mediaType/currently-free', component: OnSaleComponent, data: { title: 'currentlyFreeAppsTitle', description: 'currentlyFreeAppsDesc'}},
  { path: ':country/:store/:mediaType/zurzeit-gratis', component: OnSaleComponent, data: { title: 'currentlyFreeAppsTitle', description: 'currentlyFreeAppsDesc'}},
  { path: ':country/:store/:mediaType/maintenant-gratuit', component: OnSaleComponent, data: { title: 'currentlyFreeAppsTitle', description: 'currentlyFreeAppsDesc'}},
  { path: ':country/:store/:mediaType/actualmente-gratis', component: OnSaleComponent, data: { title: 'currentlyFreeAppsTitle', description: 'currentlyFreeAppsDesc' } },

  { path: ':country/:store/:mediaType/wishlist', component: WishlistComponent, data: { title: 'wishlistTitle', description: 'wishlistDesc' }},
  { path: ':country/:store/:mediaType/wunschliste', component: WishlistComponent, data: { title: 'wishlistTitle', description: 'wishlistDesc' } },
  { path: ':country/:store/:mediaType/liste-de-souhaits', component: WishlistComponent, data: { title: 'wishlistTitle', description: 'wishlistDesc' }},
  { path: ':country/:store/:mediaType/lista-de-deseos', component: WishlistComponent, data: { title: 'wishlistTitle', description: 'wishlistDesc' } },

  { path: ':country/:store/:mediaType/my-purchases', component: MyPurchasesComponent, data: { title: 'myPurchasesTitle', description: 'myPurchasesDesc' }},
  { path: ':country/:store/:mediaType/meine-kaeufe', component: MyPurchasesComponent, data: { title: 'myPurchasesTitle', description: 'myPurchasesDesc' }},
  { path: ':country/:store/:mediaType/mes-achats', component: MyPurchasesComponent, data: { title: 'myPurchasesTitle', description: 'myPurchasesDesc' }},
  { path: ':country/:store/:mediaType/mis-compras', component: MyPurchasesComponent, data: { title: 'myPurchasesTitle', description: 'myPurchasesDesc' } },

  { matcher: publicListDetailMatcher, component: CustomListDetailComponent, data: { noindex: true } },
  { matcher: publicListCategoryMatcher, component: MyListsComponent, data: { noindex: true } },
  { matcher: discoverListSectionMatcher, component: MyListsComponent, data: { noindex: true } },
  { matcher: discoverListsBaseMatcher, component: MyListsComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/public-lists/:listId/:listSlug', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/public-lists/:listId', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/community-lists/:listId/:listSlug', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/community-lists/:listId', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/lists/:listId/:listSlug', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/lists/:listId', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/listen/:listId/:listSlug', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/listen/:listId', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/my-lists/:listId/:listSlug', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/my-lists/:listId', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/meine-listen/:listId/:listSlug', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/meine-listen/:listId', component: CustomListDetailComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/lists', component: MyListsComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/listen', component: MyListsComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/public-lists', component: MyListsComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/community-lists', component: MyListsComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/my-lists', component: MyListsComponent, data: { noindex: true } },
  { path: ':country/:store/:mediaType/meine-listen', component: MyListsComponent, data: { noindex: true } },

  { path: ':country/:store/:mediaType/blocklist', component: BlocklistComponent, data: { title: 'blocklist', description: 'blocklistHeaderText' }},
  { path: ':country/:store/:mediaType/blockliste', component: BlocklistComponent, data: { title: 'blocklist', description: 'blocklistHeaderText' }},
  { path: ':country/:store/:mediaType/liste-exclusion', component: BlocklistComponent, data: { title: 'blocklist', description: 'blocklistHeaderText' }},
  { path: ':country/:store/:mediaType/lista-de-bloqueados', component: BlocklistComponent, data: { title: 'blocklist', description: 'blocklistHeaderText' } },

  { path: ':country/:store/:mediaType/detail-view/:id', component: DetailViewComponent },
  { path: ':country/:store/:mediaType/:id/:slug', component: DetailViewComponent },
  { path: ':country/:store/:mediaType/:id', component: DetailViewComponent },

  { path: ':country/wishlist', component: WishlistComponent, data: { title: 'wishlistTitle', description: 'wishlistDesc' }},
  { path: ':country/wunschliste', component: WishlistComponent, data: { title: 'wishlistTitle', description: 'wishlistDesc' }},
  { path: ':country/liste-de-souhaits', component: WishlistComponent, data: { title: 'wishlistTitle', description: 'wishlistDesc' }},
  { path: ':country/lista-de-deseos', component: WishlistComponent, data: { title: 'wishlistTitle', description: 'wishlistDesc' } },

  { path: ':country/prime-video-deals', component: PrimeVideoDealsComponent, data: { title: 'primeVideoDealsTitle', description: 'primeVideoDealsDesc' } },
  { path: ':country/soldes-prime-video', component: PrimeVideoDealsComponent, data: { title: 'primeVideoDealsTitle', description: 'primeVideoDealsDesc' } },
  { path: ':country/ofertas-prime-video', component: PrimeVideoDealsComponent, data: { title: 'primeVideoDealsTitle', description: 'primeVideoDealsDesc' } },

  { path: ':country/itunes-movie-deals', component: ItunesMovieDealsComponent, data: { title: 'itunesMovieDealsTitle', description: 'iTunesMovieDealsDesc' } },
  { path: ':country/itunes-film-angebote', component: ItunesMovieDealsComponent, data: { title: 'itunesMovieDealsTitle', description: 'iTunesMovieDealsDesc' } },
  { path: ':country/soldes-films-itunes', component: ItunesMovieDealsComponent, data: { title: 'itunesMovieDealsTitle', description: 'iTunesMovieDealsDesc' } },

  { path: ':country/itunes-movies-in-4k', component: ItunesMovies4KComponent, data: { title: 'itunesMovies4KTitle', description: 'iTunesMovies4KDesc' } },
  { path: ':country/itunes-filme-in-4k', component: ItunesMovies4KComponent, data: { title: 'itunesMovies4KTitle', description: 'iTunesMovies4KDesc' } },
  { path: ':country/soldes-films-itunes-4k', component: ItunesMovies4KComponent, data: { title: 'itunesMovies4KTitle', description: 'iTunesMovies4KDesc' } },
  { path: ':country/peliculas-de-itunes-en-4k', component: ItunesMovies4KComponent, data: { title: 'itunesMovies4KTitle', description: 'iTunesMovies4KDesc' } },

  { path: ':country/blog', component: BlogComponent },
  { path: ':country/blogue', component: BlogComponent },

  { path: ':country/down', component: ServerDownComponent },

  { path: ':country/search', component: SearchComponent, data: { title: 'searchTitle', description: 'searchDesc' }},
  { path: ':country/suche', component: SearchComponent, data: { title: 'searchTitle', description: 'searchDesc' }},
  { path: ':country/recherche', component: SearchComponent, data: { title: 'searchTitle', description: 'searchDesc' }},
  { path: ':country/buscar', component: SearchComponent, data: { title: 'searchTitle', description: 'searchDesc' } },

  { path: ':country/gift-card-deals', component: GiftCardsComponent, data: { title: 'giftCardDealsTitle', description: 'giftCardDealsDesc' }},
  { path: ':country/karten-rabatte', component: GiftCardsComponent, data: { title: 'giftCardDealsTitle', description: 'giftCardDealsDesc' }},
  { path: ':country/cartes-cadeaux', component: GiftCardsComponent, data: { title: 'giftCardDealsTitle', description: 'giftCardDealsDesc' }},
  { path: ':country/ofertas-tarjetas-de-regalo', component: GiftCardsComponent, data: { title: 'giftCardDealsTitle', description: 'giftCardDealsDesc' } },

  { path: ':country/imprint', component: ImprintComponent },
  { path: ':country/impressum', component: ImprintComponent },
  { path: ':country/mentions-legales', component: ImprintComponent },

  { path: ':country/contact', component: ContactComponent },
  { path: ':country/kontakt', component: ContactComponent },
  { path: ':country/contacto', component: ContactComponent },

  { path: ':country/disclaimer', component: DisclaimerComponent },
  { path: ':country/desistement', component: DisclaimerComponent },
  { path: ':country/haftungsausschluss', component: DisclaimerComponent },
  { path: ':country/aviso-legal', component: DisclaimerComponent },

  { path: ':country/privacy-policy', component: PrivacyPolicyComponent },
  { path: ':country/datenschutzbestimmungen', component: PrivacyPolicyComponent },
  { path: ':country/politique-de-confidentialite', component: PrivacyPolicyComponent },
  { path: ':country/política-de-privacidad', component: PrivacyPolicyComponent },

  { path: ':country/error', component: ErrorComponent },
  { path: ':country/404', component: PageNotFoundComponent, data: { noindex: true } },
  { path: '**', redirectTo: '/404' }
];

@NgModule({
  imports: [
    RouterModule.forRoot(
      appRoutes,
      {
        enableTracing: false, // <-- true debugging purposes only
      }
    )
  ],
  exports: [
    RouterModule
  ]
})
export class AppRoutingModule {}
